import { cx } from '../atoms/cx.ts'

import {
  AXIS_LEFT,
  AXIS_TOP,
  CELL_H,
  CELL_W,
  cellOrigin,
  GAP_X,
  GAP_Y,
  ORIGIN_X,
  ORIGIN_Y,
  TOP_ROW_H,
  trayAxes,
} from './trayGeometry.ts'

import './figures.css'

/**
 * 하이픽 트레이 3×3 + RSLOT — 와이어프레임 `traySVG()` 1318행.
 *
 * 실물 배치 순서는 `[A1 A2 A3] [B1 B2 B3] [C1 C2 C3]` 이고 위 줄 왼쪽이 RSLOT(평형추 R″),
 * 오른쪽이 나사 구멍 2개다. 이 순서를 바꾸지 마라 — 실물 사진에서 온 것이다(§16-6).
 * ⚠ 와이어프레임 표기는 `[1a 2a 3a] [1b 2b 3b] [4a 4b R]` 였다. 화면 표기는 서버 웰 이름으로
 *   통일했다(eric 2026-09-28). 칸 이름은 호출부가 넘기고 이 그림은 이름을 짓지 않는다.
 *
 * 🔴 상태에 따라 **도형이 바뀌지 않는다**. 빈 칸도 같은 자리에 점선으로 남는다(§1-2).
 */
/** `active` = 지금 그 공정이 이 챔버를 다룬다(서버 트레이 뷰 `chambers[].active` · 2026-10-07) */
export type TrayVerdict = 'pass' | 'fail' | 'pending' | 'moved' | 'active'

export interface TrayCellView {
  /** 칸 이름 — 서버 웰 이름(`A1` … `C3`). 그림은 받은 그대로 적는다 */
  slot: string
  /** 대조(Reference) 챔버 칸인가. 안쪽 면을 대조 무늬로 그린다 */
  reference?: boolean | undefined
  /** 그 칸에 챔버가 실제로 있는가. 없으면 점선 빈 자리 */
  present: boolean
  /** 칸 안 작은 글씨(시료 ID 또는 현재 소재). 마스킹은 호출부가 끝낸 값을 넘긴다 */
  sublabel?: string
  /** 비전 판정 표식 */
  verdict?: TrayVerdict | null
  /** 칸 가운데 이름 — 시료 표지(`P1` · `REF`). `chamberOutline` 모드의 챔버 든 칸에만 그린다 */
  label?: string
  /** 시료 묶음 색 번호(1 … 4) — 같은 시료 칸끼리 같은 색(`figures.css` `--sample-group-*`). `chamberOutline` 모드만 */
  group?: number | undefined
  /**
   * 이름(`label`) 아래 작은 글씨 — 작업 추가 창이 «그 웰에 고른 시료 번호»를 적는다(2026-10-21 · 웰별 고르기 2026-10-27). `chamberOutline` 모드의 이름 있는 칸만.
   * 🔴 `sublabel` 과 다른 칸이다 — `sublabel`(시료 ID)은 이 모드에서 일부러 안 그린다(모니터링이 그 값을 싣는다 · 위 `chamberOutline` 주석).
   *    주는 쪽만 그려진다 — 다른 화면은 이 키를 싣지 않아 종전 그대로다. 마스킹 · 줄임은 호출부가 끝낸 값을 넘긴다
   */
  caption?: string | undefined
}

export interface TrayFigureProps {
  cells: readonly TrayCellView[]
  /** RSLOT 에 평형추가 적재됐는가 */
  counterweightLoaded?: boolean
  /** RSLOT 빈 자리에 적을 짧은 글(「평형추 적재」). 없으면 `RSLOT` 만 */
  counterweightHint?: string
  /** 접근성 이름 */
  label: string
  /**
   * **챔버가 든 칸을 굵은 먹색 테두리 사각형 하나로**(콘솔 장비 탭 · eric 2026-09-28 「장비 모드에서.. 챔버는
   * 굳이 3d 아니어도 돼.. 그냥 굵은 검은 테두리면 돼.. 시료 부분에서만 3d 쓰고」).
   *
   * 🔴 **안 켜면 종전 그대로다** — 칸 안 셸·안쪽 면·칸 이름을 SVG 로 그린다. 켜는 곳은 `FocusFigure` 의
   *    `realFigures`(그 깃발을 켜는 곳은 통합 콘솔 장비 탭 `EquipmentSheet`)와 모니터링 시료 서랍의 트레이
   *    (`MonitoringSampleDrawer` · eric 2026-10-06 「챔버 말고.. 트레이를」) 둘이다.
   * 🔴 **도형에 상태 색을 입히지 않는다.** 테두리는 먹색(`--color-fg`) 한 가지이고, 판정은 종전처럼
   *    표지(`VerdictMark`)가 글리프로 말한다. 작은 글씨(`sublabel` — 시료 ID)는 안 적는다.
   * 🔴 **칸 이름은 칸 안이 아니라 그림 밖 엑셀식 축이다**(eric 2026-10-03 「A, B, C, 1, 2, 3 … 소모품처럼.. 바깥에」) —
   *    위 머리에 열 숫자 · 왼쪽에 행 글자(`trayAxes()`). 칸마다 `<title>` 로 이름이 남는다. 이름이 웰 꼴로 안 풀리면
   *    종전대로 칸 왼쪽 위에 적는다.
   * 🔴 **겉모습은 디자인팀 시안 `traySVG()`** 다(eric 2026-10-02 「그림들 다 수정해줘.. 디자인팀에서 제공한거」) —
   *    칸 모서리 5 · 칸 이름 자리 (7, 14) · 「옮겨 갔다」는 표지 없이 챔버 자리 점선(2026-10-15) · 평형추 칸은 실선 칸 + 「R″」
   *    (판정 표지 없음). 선 · 면 색은 `figures.css` 「콘솔 트레이」 절의 토큰이다.
   * ⚠ 2026-09-27~28 사이 이 자리는 시료 탭의 3D 챔버(`ChamberView`)를 `foreignObject` 로 세웠다 — eric 이
   *   뒤집어 걷었다. 장비 탭에 3D 캔버스를 되살리지 마라.
   */
  chamberOutline?: boolean | undefined
  /**
   * 칸을 누를 수 있게 한다(작업 추가 창의 웰별 시료 선택 · eric 2026-10-27 「해당 칸 또는 웰 별로」).
   * 🔴 **안 주면 종전 그대로다** — 그림은 `role="img"` 한 덩어리이고 칸은 누를 수 없다. 주면 그림이 `role="group"` 이 되고
   *    `cellPickLabel` 이 이름을 준 칸만 `role="button"`(Tab · Enter · Space)이 된다. 둘째 인자 = 누른 칸(포커스 복귀 자리)
   */
  onCellPick?: ((slot: string, target: SVGGElement) => void) | undefined
  /** 누를 칸의 접근 이름(예: 「P2 · A2 · 비어 있음」). `undefined` 면 그 칸은 누를 수 없다 */
  cellPickLabel?: ((cell: TrayCellView) => string | undefined) | undefined
  /** 누른 칸 가운데 지금 곁 창이 열린 칸 — 그 칸 단추가 `aria-expanded="true"` 다(다른 칸은 `false`) */
  openPick?: string | undefined
  /**
   * **묶음 틀** — 같은 시료 묶음(P1 = A1 · B1)의 칸들을 한 틀로 감싼다(작업 추가 창 · eric 2026-10-29 「P1 두 개의 환자 번호가 같아야 하잖아」).
   * 칸 뒤에 묶음 칸들을 덮는 둥근 사각형 하나(칸 사이 틈까지 묶음 면으로 채운다)를 깐다 — 두 칸이 한 덩어리로 읽힌다.
   * 🔴 **안 주면 종전 그대로다** — 모니터링 · 장비 판 · 작업 상세 서랍은 주지 않는다. `chamberOutline` 모드에서만 그린다.
   * 묶음의 칸이 같은 행 또는 같은 열로 이어진 사각형을 이룰 때만 그린다(아니면 그 묶음은 건너뛴다 — 엉뚱한 칸을 덮지 않게).
   */
  groupFrames?:
    readonly { label: string; wells: readonly string[]; group?: number | undefined }[] | undefined
}

/**
 * **챔버 한 칸의 크기.** 🔴 이 수가 곧 「챔버 한 개가 어떻게 생겼나」다 —
 * 시료 탭의 단독 챔버 그림(`ChamberFigure`)이 **여기서 들여다 쓴다**. 도형을 두 벌로 만들지
 * 않으려고 내보낸다. 값을 고치면 두 그림이 같이 따라온다. 수 자체는 `trayGeometry.ts` 한 곳이다.
 */
export const TRAY_CELL_W = CELL_W
export const TRAY_CELL_H = CELL_H

const CW = TRAY_CELL_W
const CH = TRAY_CELL_H
const GX = GAP_X
const GY = GAP_Y
const X0 = ORIGIN_X
const Y0 = ORIGIN_Y
const TOP_H = TOP_ROW_H
const W = X0 * 2 + CW * 3 + GX * 2
const H = Y0 * 2 + TOP_H + CH * 3 + GY * 2

/*
 * 실물 트레이(위에서 찍은 사진 · eric 2026-10-19 「이거.. 트레이 모양인데.. 이렇게 수정해줘」) — 바깥 테는 몸체 상자(x 6 … W−6 · y 4 … H−4).
 * 귀는 둥글고(R) 오른쪽 위 노치는 폭 · 깊이가 트레이의 약 0.12 · 0.12 이고 안쪽 귀가 둥글게 파였다(곡선 RN). 숫자는 사진 비율이다
 */
const BODY_R = 6
const NOTCH_W = 30
const NOTCH_D = 30
const NOTCH_R = 12
const TRAY_BODY_PATH = [
  `M6 ${String(4 + BODY_R)}`,
  `A${String(BODY_R)} ${String(BODY_R)} 0 0 1 ${String(6 + BODY_R)} 4`,
  `H${String(W - 6 - NOTCH_W)}`,
  `V${String(4 + NOTCH_D - NOTCH_R)}`,
  `A${String(NOTCH_R)} ${String(NOTCH_R)} 0 0 0 ${String(W - 6 - NOTCH_W + NOTCH_R)} ${String(4 + NOTCH_D)}`,
  `H${String(W - 6 - BODY_R)}`,
  `A${String(BODY_R)} ${String(BODY_R)} 0 0 1 ${String(W - 6)} ${String(4 + NOTCH_D + BODY_R)}`,
  `V${String(H - 4 - BODY_R)}`,
  `A${String(BODY_R)} ${String(BODY_R)} 0 0 1 ${String(W - 6 - BODY_R)} ${String(H - 4)}`,
  `H${String(6 + BODY_R)}`,
  `A${String(BODY_R)} ${String(BODY_R)} 0 0 1 6 ${String(H - 4 - BODY_R)}`,
  'Z',
].join(' ')
/** 윗줄(RSLOT 줄) 세로 가운데 — 구멍 · 손잡이 기둥이 이 높이에 선다 */
const TOP_MID = Y0 + (TOP_H - 6) / 2
/**
 * 손잡이 — 2번째 열 가운데에 «마름모»(정사각을 45° 세움)로 서고, 나사 구멍 둘이 그 좌우 대칭이다. 마름모와 두 구멍이 **2번째 열 폭 안에** 다 든다
 * (eric 2026-10-19 「두번째 열에.. 마름모 사각형과 좌우의 두개의 원이 2번째 열의 너비에 다 들어가게」 · 앞서 「손잡이 좌우에 구멍 대칭」).
 * 열 반폭 36 = 구멍 거리 27 + 구멍 반지름 4.5 + 여유 · 마름모 반대각 = 한 변 24 × √2 ÷ 2 ≈ 17(구멍과 5 남짓 띈다)
 */
const POST = 24
const POST_X = X0 + CW + GX + CW / 2
const HOLE_DX = 27
const HOLE_R = 4.5
const HOLE_XS = [POST_X - HOLE_DX, POST_X + HOLE_DX] as const

/**
 * 트레이 상자 한 장의 높이(px).
 *
 * 🔴 **나란히 놓는 화면에서 «그림 기준 높이»가 이것이다**(eric 지시 2026-09-21 「트레이랑
 *    높이 거의 동일하게 해줘」). CSS 쪽 짝은 `figures.css` 의 `--fig-std-h-n` 이고,
 *    `figures.test.tsx` 가 두 값이 같은지 지킨다 — 한쪽만 고치면 걸린다.
 */
export const TRAY_FIGURE_HEIGHT = H

/**
 * 축 라벨 여백 · 평면도 좌표는 `trayGeometry.ts`(`AXIS_LEFT` · `AXIS_TOP` · `cellAnchor`)가 한 곳에서 쥔다.
 * 🔴 **몸체 좌표는 그대로 두고 `viewBox` 를 음수 쪽으로 넓힌다** — 칸 · 표지 · 시험의 좌표가 축 유무로 안 흔들린다.
 */
/** 평형추 표기 — RSLOT 칸과 로터의 평형추 자리가 같은 글자를 쓴다 */
export const COUNTERWEIGHT_MARK = 'R″'

const VERDICT_GLYPH: Record<TrayVerdict, string> = {
  pass: '✓',
  fail: '✗',
  moved: '✓',
  pending: '◌',
  active: '●',
}

/** 표지 원 반지름 — ◌ · 진행 중(깜빡임) · 통과(· 종전 길의 옮겨 감 ✓)가 같다(eric 2026-10-08 「원이 크기가 동일해야 해」) */
const MARK_R = 7
/** 체크 선 — 「통과」 표지 */
const checkPath = (cx: number, cy: number) => `M${String(cx - 3.2)} ${String(cy)}l2.4 2.4 4.2-4.4`

export function VerdictMark({
  x,
  y,
  w,
  verdict,
}: {
  x: number
  y: number
  w: number
  verdict: TrayVerdict
}) {
  const cx = x + w - 8
  const cy = y + 8
  const markClass =
    verdict === 'fail'
      ? 'fig-vm__mark fig-vm__mark--fail'
      : verdict === 'moved'
        ? 'fig-vm__mark fig-vm__mark--moved'
        : verdict === 'pending'
          ? 'fig-vm__mark fig-vm__mark--pending'
          : verdict === 'active'
            ? 'fig-vm__mark fig-vm__mark--active'
            : 'fig-vm__mark fig-vm__mark--pass'
  const glyphClass =
    verdict === 'fail'
      ? 'fig-vm__glyph fig-vm__glyph--invert'
      : 'fig-vm__glyph fig-vm__glyph--muted'

  if (verdict === 'pass')
    /*
     * 통과 — 채운 원 + 흰 체크 선, 색은 성공 초록(eric 2026-10-08 「비전 통과 체크 표시가 조금 스타일이 다른데..
     * 초록색 둥근 안에 체크가 보이게」). 글자 ✓ 대신 선으로 그린다
     */
    return (
      <>
        <circle className={markClass} cx={cx} cy={cy} r={MARK_R} />
        <path className="fig-vm__check fig-vm__check--pass" d={checkPath(cx, cy)} />
      </>
    )
  /* 대기 — 검은 실선 원, 안은 흰색, 글리프 없음(eric 2026-10-08 「대기는 동그라미 검정색(안에는 흰색)」) */
  if (verdict === 'pending') return <circle className={markClass} cx={cx} cy={cy} r={MARK_R} />
  /*
   * 진행 중 — 테마색으로 채운 원이 깜빡인다(eric 2026-10-15 「진행 중은.. 깜빡거리는 걸로 대체해줘」 — 종전 ▶ 글리프는 걷었다).
   * 실행 중 상태 칩의 깜빡이는 점과 같은 뜻 · 같은 박자(`--dur-pulse` · 모션 줄임이면 멈춘 채 채운 원). 글자는 범례가 말한다
   */
  if (verdict === 'active')
    return <circle className={`${markClass} fig-vm__mark--blink`} cx={cx} cy={cy} r={MARK_R} />
  return (
    <>
      <circle className={markClass} cx={cx} cy={cy} r={MARK_R} />
      <text className={glyphClass} textAnchor="middle" x={cx} y={y + 11.5}>
        {VERDICT_GLYPH[verdict]}
      </text>
    </>
  )
}

export function TrayFigure({
  cells,
  counterweightLoaded = false,
  counterweightHint,
  label,
  chamberOutline = false,
  onCellPick,
  cellPickLabel,
  openPick,
  groupFrames,
}: TrayFigureProps) {
  const rsW = CW
  const rsH = TOP_H - 6
  /*
   * 칸 이름 자리 — 콘솔 장비 탭은 시안 `traySVG()` 의 (7, 14)(글자가 한 단 커서 한 칸 안쪽). 다른 길은 종전 (6, 12).
   * ⚠ 이 그림의 «모양» 갈래는 `chamberOutline` 하나다 — 그 깃발을 켜는 곳은 콘솔 장비 탭과 모니터링 시료 서랍이다.
   */
  const labelDx = chamberOutline ? 7 : 6
  const labelDy = chamberOutline ? 14 : 12
  /*
   * 🔴 **칸 이름 → 그림 밖 축**(콘솔 장비 탭 길 한정 · eric 2026-10-03). 축이 서면 칸 안 이름을 안 적고, 칸마다 `<title>` 로
   *    이름을 남긴다(가리키면 뜨고 DOM 에서 읽힌다). 이름이 웰 꼴로 안 풀리면 축 없이 종전대로 칸 안에 적는다.
   * ⚠ RSLOT(윗줄 평형추 자리)은 A~C · 1~3 격자 밖 칸이라 축이 이름을 대신하지 못한다 — 칸 안 표기 그대로다.
   */
  const axes = chamberOutline ? trayAxes(cells) : null
  const padL = axes === null ? 0 : AXIS_LEFT
  const padT = axes === null ? 0 : AXIS_TOP
  const named = axes === null

  /** 3×3 칸 하나 */
  const drawCell = (cell: TrayCellView, n: number) => {
    const { x, y } = cellOrigin(n)
    const reference = cell.reference === true

    if (!cell.present) {
      /*
       * 「옮겨 갔다」(콘솔 장비 판) — 표지 없이 그 칸에 챔버 자리만 점선으로 남긴다(eric 2026-10-15 「옮겨 감 이건 없애고.. 해당 트레이 부분에
       * 점선 형태로 간단하게」). 빈 칸(옅은 면 · 가는 점선)과 갈리게 면 없이 챔버 테 굵기 · 본문 색 점선이다. 종전 채운 원 + 체크(`MovedCheck`)는 걷었다
       */
      const movedGhost = chamberOutline && cell.verdict === 'moved'
      return (
        <g
          data-slot={chamberOutline ? cell.slot : undefined}
          data-verdict={movedGhost ? 'moved' : undefined}
          key={cell.slot}
        >
          {named ? null : <title>{cell.slot}</title>}
          <rect
            className={movedGhost ? 'fig-tray__slot fig-tray__slot--moved' : 'fig-tray__slot'}
            height={CH}
            rx={5}
            width={CW}
            x={x}
            y={y}
          />
          {movedGhost ? null : cell.verdict ? (
            <VerdictMark verdict={cell.verdict} w={CW} x={x} y={y} />
          ) : null}
          {cell.sublabel === undefined ? null : (
            <text className="fig-tray__ghost" textAnchor="middle" x={x + CW / 2} y={y + CH / 2 + 3}>
              {cell.sublabel}
            </text>
          )}
          {named ? (
            <text className="fig-tray__ghost" x={x + labelDx} y={y + labelDy}>
              {cell.slot}
            </text>
          ) : null}
        </g>
      )
    }

    if (chamberOutline) {
      return (
        <g
          className={cx(
            'fig-tray__cell--chamber',
            /* 지금 다루는 챔버(이동 · 장착 중 · 서버 `active`) — 칸 테두리가 깜빡인다(eric 2026-10-15) */
            cell.verdict === 'active' && 'fig-tray__cell--active',
          )}
          data-group={cell.group}
          data-slot={cell.slot}
          data-verdict={cell.verdict ?? undefined}
          key={cell.slot}
        >
          {named ? null : <title>{cell.slot}</title>}
          {/* 🔴 실선 — 점선(`__slot`)은 「아직 없음」 한 뜻이라 챔버가 든 칸에 쓰지 않는다 */}
          <rect className="fig-tray__outline" height={CH} rx={5} width={CW} x={x} y={y} />
          {named ? (
            <text className="fig-tray__corner" x={x + labelDx} y={y + labelDy}>
              {cell.slot}
            </text>
          ) : null}
          {cell.label === undefined ? null : (
            <text
              className="fig-tray__name"
              textAnchor="middle"
              x={x + CW / 2}
              y={y + CH / 2 + (cell.caption === undefined ? 4 : -2)}
            >
              {cell.label}
            </text>
          )}
          {cell.label === undefined || cell.caption === undefined ? null : (
            <text
              className="fig-tray__caption"
              textAnchor="middle"
              x={x + CW / 2}
              y={y + CH / 2 + 12}
            >
              {cell.caption}
            </text>
          )}
          {/*
                🔴 진행 중은 모서리 표지가 아니라 «칸 테두리»가 깜빡인다(eric 2026-10-15 「진행 중인것이 챔버 오른쪽에 보이는 것이 아니라..
                   해당 영역 테두리를 깜빡 거리는 거야」). 테두리 위에 테마색 테 한 겹을 얹어 그것만 깜빡인다 — 칸 면 · 이름은 그대로 선다
              */}
          {cell.verdict === 'active' ? (
            <rect className="fig-tray__blink" height={CH} rx={5} width={CW} x={x} y={y} />
          ) : cell.verdict ? (
            <VerdictMark verdict={cell.verdict} w={CW} x={x} y={y} />
          ) : null}
        </g>
      )
    }

    return (
      <g key={cell.slot}>
        <rect className="fig-tray__shell" height={CH} rx={4} width={CW} x={x} y={y} />
        <rect
          className={`fig-tray__inner${reference ? ' fig-tray__inner--ref' : ''}`}
          height={CH - 10}
          rx={2}
          width={CW - 8}
          x={x + 4}
          y={y + 6}
        />
        <text className="fig-tray__lab" textAnchor="middle" x={x + CW / 2} y={y + CH / 2 - 2}>
          {cell.slot}
        </text>
        <text className="fig-tray__sub" textAnchor="middle" x={x + CW / 2} y={y + CH / 2 + 12}>
          {cell.sublabel ?? ''}
        </text>
        {cell.verdict ? <VerdictMark verdict={cell.verdict} w={CW} x={x} y={y} /> : null}
      </g>
    )
  }

  return (
    <svg
      className="fig fig-tray"
      height={H + padT}
      role={onCellPick === undefined ? 'img' : 'group'}
      aria-label={label}
      viewBox={`${String(-padL)} ${String(-padT)} ${String(W + padL)} ${String(H + padT)}`}
      width={W + padL}
    >
      {axes === null ? null : (
        <g className="fig-tray__axes" data-part="axes">
          {/* 머리 — 몸체 위 테(y 4)와 여백 위 끝 사이 가운데 · 옆 — 몸체 왼쪽 테(x 6)와 여백 왼쪽 끝 사이 가운데 */}
          {axes.head.map((a) => (
            <text
              className="fig-tray__axis"
              data-axis="col"
              dominantBaseline="central"
              key={`c${a.label}`}
              textAnchor="middle"
              x={a.pos}
              y={(4 - padT) / 2}
            >
              {a.label}
            </text>
          ))}
          {axes.side.map((a) => (
            <text
              className="fig-tray__axis"
              data-axis="row"
              dominantBaseline="central"
              key={`r${a.label}`}
              textAnchor="middle"
              x={(6 - padL) / 2}
              y={a.pos}
            >
              {a.label}
            </text>
          ))}
        </g>
      )}
      {/* 몸체 — 둥근 귀 + 우상단 노치(안으로 둥글게 파인 곡선)가 있는 실물 형상(eric 2026-10-19 위에서 찍은 사진) */}
      <path className="fig-tray__body" d={TRAY_BODY_PATH} />

      {/* 윗줄 — RSLOT(왼쪽) + 나사 구멍 2. `data-rslot` = 원심 그리퍼가 평형추를 집고 놓는 자리를 재는 표지 */}
      <g data-rslot="true">
        {counterweightLoaded && chamberOutline ? (
          /*
           * 콘솔 장비 탭 — 시안 `tr-on` 칸 + 가운데 「R″」. 🔴 **판정 표지를 달지 않는다** — 평형추가 «놓였다»는
           * 판정(비전 통과)이 아니다. 종전 길은 여기에 통과 「✓」를 그렸는데 그 값은 어느 응답에도 없다.
           */
          <>
            <rect className="fig-tray__outline" height={rsH} rx={5} width={rsW} x={X0} y={Y0} />
            <text
              className="fig-tray__corner"
              textAnchor="middle"
              x={X0 + rsW / 2}
              y={Y0 + rsH / 2 + 4}
            >
              {COUNTERWEIGHT_MARK}
            </text>
          </>
        ) : counterweightLoaded ? (
          <>
            <rect className="fig-tray__shell" height={rsH} rx={5} width={rsW} x={X0} y={Y0} />
            <rect
              className="fig-tray__inner"
              height={rsH - 8}
              rx={3}
              width={rsW - 8}
              x={X0 + 4}
              y={Y0 + 4}
            />
            <text
              className="fig-tray__lab"
              textAnchor="middle"
              x={X0 + rsW / 2}
              y={Y0 + rsH / 2 + 4}
            >
              {COUNTERWEIGHT_MARK}
            </text>
            <VerdictMark verdict="pass" w={rsW} x={X0} y={Y0} />
          </>
        ) : (
          <>
            <rect className="fig-tray__slot" height={rsH} rx={5} width={rsW} x={X0} y={Y0} />
            {counterweightHint === undefined ? (
              <text
                className="fig-tray__ghost"
                textAnchor="middle"
                x={X0 + rsW / 2}
                y={Y0 + rsH / 2 + 4}
              >
                <tspan className="fig-tray__rslot-label">RSLOT</tspan>
              </text>
            ) : (
              <>
                <text
                  className="fig-tray__lab"
                  textAnchor="middle"
                  x={X0 + rsW / 2}
                  y={Y0 + rsH / 2 - 1}
                >
                  {COUNTERWEIGHT_MARK}
                </text>
                <text
                  className="fig-tray__sub"
                  textAnchor="middle"
                  x={X0 + rsW / 2}
                  y={Y0 + rsH / 2 + 10}
                >
                  {counterweightHint}
                </text>
              </>
            )}
          </>
        )}
      </g>
      {/*
       * 윗줄 2번째 열 — 6축이 트레이를 집는 손잡이(마름모 하나 · eric 「마름모 사각형 두개인데.. 하나만」) + 좌우 대칭 나사 구멍 둘(eric 2026-10-19 사진 · 「두번째 열에.. 마름모」).
       * 종전 구멍은 2 · 3 열 왼쪽에 서서 오른쪽으로 몰려 있었다
       */}
      <g data-part="grip-post" transform={`rotate(45 ${String(POST_X)} ${String(TOP_MID)})`}>
        <rect
          className="fig-tray__post"
          height={POST}
          rx={3}
          width={POST}
          x={POST_X - POST / 2}
          y={TOP_MID - POST / 2}
        />
      </g>
      <circle className="fig-tray__hole" cx={HOLE_XS[0]} cy={TOP_MID} r={HOLE_R} />
      <circle className="fig-tray__hole" cx={HOLE_XS[1]} cy={TOP_MID} r={HOLE_R} />

      {/* 묶음 틀 — 칸보다 먼저(뒤에) 깐다 */}
      {chamberOutline && groupFrames !== undefined
        ? groupFrames.map((frame) => {
            const at = frame.wells.map((w) => cells.findIndex((c) => c.slot === w))
            if (at.length < 2 || at.some((n) => n < 0)) return null
            const spots = at.map((n) => cellOrigin(n))
            const cols = new Set(spots.map((p) => p.col))
            const rows = new Set(spots.map((p) => p.row))
            const span = (set: Set<number>) => Math.max(...set) - Math.min(...set) + 1
            /* 꽉 찬 사각형(빈 구멍 없이)일 때만 — 칸 수 = 열 폭 × 행 높이 */
            if (span(cols) * span(rows) !== spots.length) return null
            const x = Math.min(...spots.map((p) => p.x))
            const y = Math.min(...spots.map((p) => p.y))
            const w = Math.max(...spots.map((p) => p.x)) + CW - x
            const h = Math.max(...spots.map((p) => p.y)) + CH - y
            return (
              <rect
                className="fig-tray__frame"
                data-frame={frame.label}
                data-group={frame.group}
                height={h}
                key={`frame-${frame.label}`}
                rx={5}
                width={w}
                x={x}
                y={y}
              />
            )
          })
        : null}

      {/* 3×3 */}
      {cells.map((cell, n) => {
        const drawn = drawCell(cell, n)
        const pickLabel = onCellPick === undefined ? undefined : cellPickLabel?.(cell)
        if (onCellPick === undefined || pickLabel === undefined) return drawn
        return (
          <g
            aria-expanded={openPick === cell.slot}
            aria-haspopup="dialog"
            aria-label={pickLabel}
            className="fig-tray__pick"
            data-pick={cell.slot}
            key={`pick-${cell.slot}`}
            onClick={(event) => onCellPick(cell.slot, event.currentTarget)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onCellPick(cell.slot, event.currentTarget)
            }}
            role="button"
            tabIndex={0}
          >
            {/* 가리키면 접근 이름 그대로(칸 안 글자는 줄인 꼴이라 전체 번호는 여기서 읽는다) */}
            <title>{pickLabel}</title>
            {drawn}
          </g>
        )
      })}
    </svg>
  )
}
